import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, CheckCircle2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveUrl } from "@/lib/apiClient";
import { useSite } from "@/context/SiteContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "w-full bg-transparent border-b border-[#E5DFD3] rounded-none px-0 py-3 text-[#1A201D] placeholder:text-[#9A8C73]/60 focus:border-[#2C3531] focus:outline-none transition-colors";
const SPECIALTIES = ["Injectables & Anti-Aging", "Advanced Skin", "Semi-Permanent Makeup", "Body Sculpting", "Wellness & IV", "Cosmetic Teeth Whitening", "Multi-Discipline"];

export default function Register() {
  const { site } = useSite();
  const settings = site?.settings;
  const [f, setF] = useState({ business_name: "", contact_name: "", email: "", password: "", phone: "", city: "", specialty: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await api.post("/public/business-applications", f);
      setDone(true);
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F3EFE7] grain relative">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#4A5550] hover:text-[#2C3531] mb-8" data-testid="register-back">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="flex items-center gap-2 mb-10">
          {settings?.logo_url ? <img src={resolveUrl(settings.logo_url)} alt="" className="h-9" /> :
            <><Sparkles className="w-5 h-5 text-[#9A8C73]" strokeWidth={1.75} /><span className="font-display text-2xl font-semibold text-[#2C3531]">{settings?.site_name || "Grow Your Bookings"}</span></>}
        </div>

        {done ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#FDFBF7] border border-[#E5DFD3] rounded-3xl p-12 text-center" data-testid="register-success">
            <CheckCircle2 className="w-14 h-14 text-[#3B5341] mx-auto mb-5" strokeWidth={1.5} />
            <h1 className="font-display text-4xl font-semibold text-[#1A201D] mb-3">Application received</h1>
            <p className="text-[#4A5550] max-w-md mx-auto leading-relaxed mb-8">Thanks for applying to grow your bookings. Our team will review your details and email you at <strong>{f.email}</strong> once your business account is approved — then you'll get your own recruitment page to bring host salons on board.</p>
            <Link to="/" className="inline-flex px-7 py-3.5 rounded-full bg-[#2C3531] text-[#FDFBF7] font-semibold hover:scale-105 transition-transform duration-300">Return home</Link>
          </motion.div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#9A8C73] mb-4">Apply as a mobile aesthetic business</p>
              <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-[#1A201D] mb-4">Turn local salons into your booking engine.</h1>
              <p className="text-[#4A5550] leading-relaxed mb-10 max-w-xl">Apply to join the platform. Once approved you'll get a dashboard plus your own branded recruitment page to bring host salons on board — no ad spend required.</p>
            </motion.div>

            <form onSubmit={submit} data-testid="register-form" className="bg-[#FDFBF7] border border-[#E5DFD3] rounded-3xl p-8 md:p-10 space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="reg-business-name" className={field} placeholder="Business name" value={f.business_name} onChange={(e) => upd("business_name", e.target.value)} required />
                <input data-testid="reg-contact-name" className={field} placeholder="Your full name" value={f.contact_name} onChange={(e) => upd("contact_name", e.target.value)} required />
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="reg-email" type="email" className={field} placeholder="Login email" value={f.email} onChange={(e) => upd("email", e.target.value)} required />
                <input data-testid="reg-password" type="password" className={field} placeholder="Create a password" value={f.password} onChange={(e) => upd("password", e.target.value)} required />
              </div>
              <p className="text-xs text-[#9A8C73] -mt-3" data-testid="reg-google-note">Tip: if you'd like to sign in with Google later, use your Google email address here.</p>
              <div className="grid sm:grid-cols-2 gap-6">
                <input data-testid="reg-phone" className={field} placeholder="Phone number" value={f.phone} onChange={(e) => upd("phone", e.target.value)} />
                <input data-testid="reg-city" className={field} placeholder="City / region" value={f.city} onChange={(e) => upd("city", e.target.value)} />
              </div>
              <div>
                <Select value={f.specialty} onValueChange={(v) => upd("specialty", v)}>
                  <SelectTrigger data-testid="reg-specialty" className="border-0 border-b border-[#E5DFD3] rounded-none px-0 shadow-none focus:ring-0"><SelectValue placeholder="Your aesthetic specialty" /></SelectTrigger>
                  <SelectContent>{SPECIALTIES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <textarea data-testid="reg-message" className={`${field} resize-none`} rows={3} placeholder="Tell us about your business (optional)" value={f.message} onChange={(e) => upd("message", e.target.value)} />
              <button type="submit" disabled={loading} data-testid="reg-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2C3531] text-[#FDFBF7] font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
              </button>
              <p className="flex items-center justify-center gap-2 text-xs text-[#9A8C73]"><ShieldCheck className="w-4 h-4" /> Reviewed & approved by our team before going live</p>
            </form>
            <p className="text-center text-sm text-[#4A5550] mt-6">Already a partner business? <Link to="/business/login" className="text-[#2C3531] font-semibold underline">Business login</Link></p>
          </>
        )}
      </div>
    </div>
  );
}
